import * as ImageSize from "utils/ImageSize";
import FocalPoint from "components/FocalPoint";
import Image from "components/Image";
import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FocalPointPropType } from "types/FocalPoint";
import classnames from "classnames";
import { useCachedImageAspectRatio } from "hooks/useCachedImageAspectRatio";
import { generateImageLink } from "utils/generateImageLink";
import styles from "./focalPointImage/FocalPointImage.css";
import { useIsMobile } from "../hooks/useIsMobile";

const FocalPointImage = React.forwardRef((props, ref) => {
  const isMobile = useIsMobile();

  const {
    cachedDataUrl,
    containThreshold,
    fitMode,
    focalPoint,
    focalPointClassName,
    manualContainerHeight,
    manualContainerWidth,
    src,
    videoId,
    ...passthroughProps
  } = props;
  const parsedImageUrl = generateImageLink(src, {
    isMobile,
    ...passthroughProps,
  });
  const cachedImageAspectRatio = useCachedImageAspectRatio(parsedImageUrl);

  const [imageAspectRatio, setImageAspectRatio] = useState(
    cachedImageAspectRatio || null,
  );

  const onLoad = useCallback(
    (event) => {
      setImageAspectRatio(
        event.target.naturalWidth / event.target.naturalHeight,
      );
    },
    [setImageAspectRatio],
  );

  const focalPointWithImageDimensions = useMemo(
    () => ({
      // The focal point doesn't care about _actual_ dimensions, it cares about
      // the aspect ratio, which is `width / height`. Therefore, using
      // `imageAspectRatio` as the `width` and `1` for the height will work for
      // the calculations. We _should_ start _just_ using `aspectRatio` for
      // focal points 🤷‍♀️
      focalX: focalPoint.focalX,
      focalY: focalPoint.focalY,
      height: 1,
      width: imageAspectRatio,
    }),
    [focalPoint.focalX, focalPoint.focalY, imageAspectRatio],
  );

  return (
    <FocalPoint
      className={classnames(focalPointClassName, {
        [styles.transparent]: imageAspectRatio == null,
      })}
      containThreshold={containThreshold}
      fitMode={fitMode}
      focalPoint={focalPointWithImageDimensions}
      manualContainerHeight={manualContainerHeight}
      manualContainerWidth={manualContainerWidth}
      videoId={videoId}
    >
      <Image
        cachedDataUrl={cachedDataUrl}
        onLoad={imageAspectRatio == null ? onLoad : undefined}
        ref={ref}
        src={src}
        {...passthroughProps}
      />
    </FocalPoint>
  );
});

FocalPointImage.propTypes = {
  cachedDataUrl: PropTypes.string,
  containThreshold: PropTypes.number,
  fitMode: PropTypes.oneOf(["contain", "cover"]).isRequired,
  focalPoint: FocalPointPropType.isRequired,
  focalPointClassName: PropTypes.string,
  manualContainerHeight: PropTypes.number,
  manualContainerWidth: PropTypes.number,
  size: PropTypes.oneOf(Object.values(ImageSize)),
  src: PropTypes.string.isRequired,
  videoId: PropTypes.number,
};

export default React.memo(FocalPointImage);
